import { supabase } from '../utils/supabase';

class InvoiceService {
  // Get all invoices for a vendor
  async getVendorInvoices(vendorId, filters = {}) {
    try {
      let query = supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            id,
            description,
            quantity,
            unit_price,
            total_price,
            sort_order
          )
        `)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      
      if (filters?.clientName) {
        query = query.ilike('client_name', `%${filters.clientName}%`);
      }
      
      if (filters?.fromDate) {
        query = query.gte('invoice_date', filters.fromDate);
      }
      
      if (filters?.toDate) {
        query = query.lte('invoice_date', filters.toDate);
      }
      
      if (filters?.minAmount) {
        query = query.gte('total_amount', filters.minAmount);
      }
      
      if (filters?.maxAmount) {
        query = query.lte('total_amount', filters.maxAmount);
      }

      // Apply pagination
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load invoices' };
    }
  }

  // Get invoice by ID
  async getInvoiceById(invoiceId) {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items (
            id,
            description,
            quantity,
            unit_price,
            total_price,
            sort_order
          ),
          vendor_profiles!inner (
            id,
            business_name,
            business_address,
            phone,
            website,
            user_profiles!inner (
              full_name,
              email
            )
          )
        `)
        .eq('id', invoiceId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load invoice' };
    }
  }

  // Create new invoice
  async createInvoice(vendorId, invoiceData) {
    try {
      // First, generate invoice number
      const { data: invoiceNumber, error: numberError } = await supabase
        .rpc('generate_invoice_number', { vendor_uuid: vendorId });

      if (numberError) {
        return { success: false, error: numberError.message };
      }

      // Create invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          vendor_id: vendorId,
          invoice_number: invoiceNumber,
          ...invoiceData
        })
        .select()
        .single();

      if (invoiceError) {
        return { success: false, error: invoiceError.message };
      }

      return { success: true, data: invoice };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to create invoice' };
    }
  }

  // Update invoice
  async updateInvoice(invoiceId, updates) {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to update invoice' };
    }
  }

  // Delete invoice
  async deleteInvoice(invoiceId) {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to delete invoice' };
    }
  }

  // Add item to invoice
  async addInvoiceItem(invoiceId, itemData) {
    try {
      // Calculate total price
      const totalPrice = itemData.quantity * itemData.unit_price;

      const { data, error } = await supabase
        .from('invoice_items')
        .insert({
          invoice_id: invoiceId,
          ...itemData,
          total_price: totalPrice
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to add invoice item' };
    }
  }

  // Update invoice item
  async updateInvoiceItem(itemId, updates) {
    try {
      // Recalculate total price if quantity or unit_price changed
      if (updates.quantity !== undefined || updates.unit_price !== undefined) {
        const { data: currentItem, error: fetchError } = await supabase
          .from('invoice_items')
          .select('quantity, unit_price')
          .eq('id', itemId)
          .single();

        if (fetchError) {
          return { success: false, error: fetchError.message };
        }

        const newQuantity = updates.quantity !== undefined ? updates.quantity : currentItem.quantity;
        const newUnitPrice = updates.unit_price !== undefined ? updates.unit_price : currentItem.unit_price;
        updates.total_price = newQuantity * newUnitPrice;
      }

      const { data, error } = await supabase
        .from('invoice_items')
        .update(updates)
        .eq('id', itemId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to update invoice item' };
    }
  }

  // Delete invoice item
  async deleteInvoiceItem(itemId) {
    try {
      const { error } = await supabase
        .from('invoice_items')
        .delete()
        .eq('id', itemId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to delete invoice item' };
    }
  }

  // Get invoice templates for vendor
  async getVendorTemplates(vendorId) {
    try {
      const { data, error } = await supabase
        .from('invoice_templates')
        .select(`
          *,
          invoice_template_items (
            id,
            description,
            unit_price,
            sort_order
          )
        `)
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load templates' };
    }
  }

  // Create invoice template
  async createTemplate(vendorId, templateData) {
    try {
      const { data, error } = await supabase
        .from('invoice_templates')
        .insert({
          vendor_id: vendorId,
          ...templateData
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to create template' };
    }
  }

  // Create invoice from template
  async createInvoiceFromTemplate(vendorId, templateId, invoiceData) {
    try {
      // Get template data
      const { data: template, error: templateError } = await supabase
        .from('invoice_templates')
        .select(`
          *,
          invoice_template_items (
            description,
            unit_price,
            sort_order
          )
        `)
        .eq('id', templateId)
        .single();

      if (templateError) {
        return { success: false, error: templateError.message };
      }

      // Generate invoice number
      const { data: invoiceNumber, error: numberError } = await supabase
        .rpc('generate_invoice_number', { vendor_uuid: vendorId });

      if (numberError) {
        return { success: false, error: numberError.message };
      }

      // Create invoice with template defaults
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          vendor_id: vendorId,
          invoice_number: invoiceNumber,
          terms_and_conditions: template.default_terms,
          notes: template.default_notes,
          tax_rate: template.default_tax_rate,
          ...invoiceData
        })
        .select()
        .single();

      if (invoiceError) {
        return { success: false, error: invoiceError.message };
      }

      // Add template items to invoice
      if (template?.invoice_template_items?.length > 0) {
        const invoiceItems = template.invoice_template_items.map(item => ({
          invoice_id: invoice.id,
          description: item.description,
          quantity: 1,
          unit_price: item.unit_price,
          total_price: item.unit_price,
          sort_order: item.sort_order
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(invoiceItems);

        if (itemsError) {
          return { success: false, error: itemsError.message };
        }
      }

      return { success: true, data: invoice };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to create invoice from template' };
    }
  }

  // Get invoice statistics for vendor
  async getInvoiceStats(vendorId) {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('status, total_amount, due_date')
        .eq('vendor_id', vendorId);

      if (error) {
        return { success: false, error: error.message };
      }

      const invoices = data || [];
      const today = new Date();
      
      const stats = {
        totalInvoices: invoices.length,
        totalAmount: invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0),
        paidAmount: invoices
          .filter(inv => inv.status === 'paid')
          .reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0),
        pendingAmount: invoices
          .filter(inv => ['draft', 'sent'].includes(inv.status))
          .reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0),
        overdueAmount: invoices
          .filter(inv => inv.status === 'sent' && new Date(inv.due_date) < today)
          .reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0),
        draftCount: invoices.filter(inv => inv.status === 'draft').length,
        sentCount: invoices.filter(inv => inv.status === 'sent').length,
        paidCount: invoices.filter(inv => inv.status === 'paid').length,
        overdueCount: invoices.filter(inv => inv.status === 'sent' && new Date(inv.due_date) < today).length
      };

      return { success: true, data: stats };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to load invoice statistics' };
    }
  }

  // Check if vendor has paid subscription for invoice features
  async checkPaidSubscription() {
    try {
      const { data, error } = await supabase
        .rpc('has_paid_subscription');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Failed to check subscription status' };
    }
  }

  // Subscribe to real-time invoice updates
  subscribeToInvoiceUpdates(vendorId, callback) {
    const subscription = supabase
      .channel('invoice_updates')
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'invoices',
          filter: `vendor_id=eq.${vendorId}`
        },
        callback
      )
      .subscribe();

    return subscription;
  }

  // Unsubscribe from real-time updates
  unsubscribeFromUpdates(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
}

// Export singleton instance
const invoiceService = new InvoiceService();
export default invoiceService;